import type { CustomApp } from "@clickdialog/product-registry";
import type {
  SolutionBlueprintV1,
  SolutionCanvasDraftV1,
  SolutionCanvasIssue,
  SolutionContractValidation,
  ProspectDemoRequestContract,
  ProspectDemoLifecycleStatus,
  ProspectDemoCreationPath,
} from "@clickdialog/solution-contract";
import type { PlatformUser, ResolvedOrganizationContext, Session, ShellContext } from "./types";
import type { SalonAgenda, SalonAvailabilitySlot, SalonAppointmentStatus, SalonAvailabilityBlock, SalonAvailabilityRule, SalonBookingRules, SalonCalComConnection, SalonCalComEventType, SalonCalComServiceBinding, SalonConversationCannedResponse, SalonConversationControlMode, SalonConversationDetail, SalonConversationLifecycle, SalonConversationOperatorState, SalonConversationPriority, SalonConversationWorkspaceConfiguration, SalonCustomer360, SalonCustomerExport, SalonCustomerImportCommitResult, SalonCustomerImportMapping, SalonCustomerImportPreview, SalonCustomerList, SalonCustomerMutationResult, SalonCustomerSegment, SalonCustomerSort, SalonInbox, SalonInboxScope, SalonInboxView, SalonMetaWhatsAppConnections, SalonOverview, SalonPublicBookingSettings, SalonReports, SalonAppointmentDeposit, SalonMercadoPagoConnection, SalonPaymentCheckout, SalonSale, SalonSaleCollection, SalonSaleReceipt, SalonSalesScreen, SalonSchedulingBusiness, SalonSchedulingLocation, SalonSchedulingResource, SalonSchedulingResourceType, SalonSchedulingServiceConfig, SalonSchedulingSettings, SalonService, SalonServiceCategory, SalonServiceResourceRequirement } from "./salons/types";
import type { SalonAssistantProductSettings, SalonAssistantReadiness, SalonAutomationRule, SalonAutomationSettings, SalonKnowledgeQueryResult, SalonKnowledgeSettings, SalonKnowledgeSource } from "./salons/types";

export interface SalonDemoSimulationDetail {
  readonly conversationId: string;
  readonly organizationId: string;
  readonly customerId: string;
  readonly phase: "idle" | "offered" | "confirming" | "booked";
  readonly appointmentId: string | null;
  readonly controlMode: "ai" | "human";
  readonly mode: "deepseek" | "guided";
  readonly model: string | null;
  readonly messages: readonly { id: string; direction: "inbound" | "outbound"; sender: string; body: string; occurredAt: string }[];
}
export interface SalonDemoSimulatorCatalog {
  readonly enabled: true;
  readonly mode: "deepseek" | "guided";
  readonly model: string | null;
  readonly customers: readonly { id: string; name: string }[];
  readonly services: readonly { id: string; name: string; duration: number; priceMinor: number }[];
  readonly sessions: readonly { conversation_id: string; customer_id: string; phase: string; appointment_id: string | null; updated_at: string }[];
}

interface ErrorBody { error?: { code?: string; message?: string; details?: Readonly<Record<string, unknown>> }; requestId?: string }
export interface CustomAppPublicToken { token: string; customAppId: string; organizationId: string; status: "active" | "revoked"; createdAt: string; revokedAt: string | null }
export interface CustomAsset { path: string; mimeType: string; bytes: number }

export interface SolutionFactoryCanvas {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly canvas: SolutionCanvasDraftV1;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly createdBy: string;
  readonly updatedBy: string;
}

export interface SolutionFactoryDraft {
  readonly id: string;
  readonly templateKey: string;
  readonly displayName: string;
  readonly lifecycle: "draft" | "candidate";
  readonly blueprint: SolutionBlueprintV1;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly createdBy: string;
  readonly updatedBy: string;
}

export interface SolutionFactoryCertification {
  readonly id: string;
  readonly sourceDraftId: string;
  readonly sourceDraftVersion: number;
  readonly templateKey: string;
  readonly displayName: string;
  readonly blueprint: SolutionBlueprintV1;
  readonly blueprintHash: string;
  readonly contractVersion: string;
  readonly validation: SolutionContractValidation;
  readonly certifiedAt: string;
  readonly certifiedBy: string;
}

export type SolutionFactoryProvisioningStatus =
  | "blocked"
  | "review_required"
  | "ready_for_future_activation";

export interface SolutionFactoryProvisioningCheck {
  readonly key: string;
  readonly kind: string;
  readonly status: "ready" | "blocked" | "manual_review";
  readonly label: string;
  readonly detail: string;
  readonly evidence?: Readonly<Record<string, unknown>>;
}

export interface SolutionFactoryProvisioningPreview {
  readonly certificationId: string;
  readonly organizationId: string;
  readonly templateKey: string;
  readonly generatedAt: string;
  readonly status: SolutionFactoryProvisioningStatus;
  readonly activationAllowed: false;
  readonly checks: readonly SolutionFactoryProvisioningCheck[];
  readonly summary: Readonly<{
    ready: number;
    blocked: number;
    manualReview: number;
  }>;
  readonly boundaries: Readonly<{
    providerMutation: false;
    productActivation: false;
    runtimeActivation: false;
    businessMutation: false;
  }>;
}

export interface SolutionFactoryProvisioningPlan {
  readonly id: string;
  readonly certificationId: string;
  readonly organizationId: string;
  readonly templateKey: string;
  readonly blueprintHash: string;
  readonly status: SolutionFactoryProvisioningStatus;
  readonly activationAllowed: false;
  readonly version: number;
  readonly preview: SolutionFactoryProvisioningPreview;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly updatedAt: string;
  readonly updatedBy: string;
}

export interface SolutionFactoryProvisioningPlanRevision {
  readonly planId: string;
  readonly version: number;
  readonly preview: SolutionFactoryProvisioningPreview;
  readonly createdAt: string;
  readonly createdBy: string;
}

export type SolutionFactoryProvisioningApplyAction =
  | Readonly<{
      kind: "agent.configure-profile";
      profileKey: "default" | "voice";
      provider: string;
      model: string;
      credential?: string;
      reasoningEffort?: "none" | "low";
      serviceTier?: "default" | "fast";
    }>
  | Readonly<{
      kind: "knowledge.ingest-approved";
      title: string;
      text: string;
    }>;

export interface SolutionFactoryProvisioningApplyDescriptor {
  readonly checkKey: string;
  readonly label: string;
  readonly detail: string;
  readonly mode: "apply" | "operator_required";
  readonly actionKind: SolutionFactoryProvisioningApplyAction["kind"] | null;
  readonly profileKey?: "default" | "voice";
}

export interface SolutionFactoryProvisioningApplyReceipt {
  readonly id: string;
  readonly provisioningPlanId: string;
  readonly provisioningPlanVersion: number;
  readonly organizationId: string;
  readonly certificationId: string;
  readonly actionKind: SolutionFactoryProvisioningApplyAction["kind"];
  readonly checkKey: string;
  readonly idempotencyKey: string;
  readonly requestFingerprint: string;
  readonly status: "started" | "applied" | "failed";
  readonly result: Readonly<Record<string, unknown>> | null;
  readonly errorCode: string | null;
  readonly startedAt: string;
  readonly completedAt: string | null;
  readonly createdBy: string;
}

export interface SolutionFactoryProvisioningApplyState {
  readonly actions: readonly SolutionFactoryProvisioningApplyDescriptor[];
  readonly receipts: readonly SolutionFactoryProvisioningApplyReceipt[];
}

export interface SolutionFactoryInstance {
  readonly id: string;
  readonly provisioningPlanId: string;
  readonly provisioningPlanVersion: number;
  readonly certificationId: string;
  readonly organizationId: string;
  readonly templateKey: string;
  readonly blueprintHash: string;
  readonly lifecycleState: "prepared";
  readonly createdAt: string;
  readonly createdBy: string;
}

export interface SolutionFactoryActivationControl {
  readonly organizationId: string;
  readonly templateKey: string;
  readonly selectedSolutionInstanceId: string;
  readonly status: "active" | "inactive";
  readonly version: number;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly updatedAt: string;
  readonly updatedBy: string;
}

export interface SolutionFactoryActivationRevision {
  readonly organizationId: string;
  readonly templateKey: string;
  readonly version: number;
  readonly selectedSolutionInstanceId: string;
  readonly status: "active" | "inactive";
  readonly action: "activate" | "deactivate" | "reactivate" | "switch";
  readonly changedAt: string;
  readonly changedBy: string;
}

export interface SolutionFactoryRuntimeAuthorization {
  readonly allowed: boolean;
  readonly mode:
    | "legacy_unmanaged"
    | "prepared_inactive"
    | "managed_inactive"
    | "active";
  readonly organizationId: string;
  readonly templateKey: string;
  readonly solutionInstanceId: string | null;
  readonly activationId: string | null;
}

export interface SolutionFactoryActivationState {
  readonly activation: Readonly<{
    id: string;
    solutionInstanceId: string;
    organizationId: string;
    templateKey: string;
    activatedAt: string;
    activatedBy: string;
  }> | null;
  readonly control: SolutionFactoryActivationControl | null;
  readonly revisions: readonly SolutionFactoryActivationRevision[];
  readonly runtimeAuthorization: SolutionFactoryRuntimeAuthorization;
}

export interface ProspectDemoRequestRecord {
  readonly id: string;
  readonly requestKey: string;
  readonly source: "prospecting-system";
  readonly campaignId: string | null;
  readonly prospectKey: string;
  readonly businessName: string;
  readonly marketKey: string;
  readonly industryKey: string;
  readonly nicheKey: string;
  readonly subnicheKey: string;
  readonly solutionKey: string;
  readonly templateKey: string | null;
  readonly referenceDemoId: string | null;
  readonly request: ProspectDemoRequestContract;
  readonly status: ProspectDemoLifecycleStatus;
  readonly creationPath: ProspectDemoCreationPath;
  readonly demoId: string | null;
  readonly demoUrl: string | null;
  readonly version: number;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly updatedAt: string;
  readonly updatedBy: string;
}

export interface ProspectDemoRequestEvent {
  readonly id: string;
  readonly requestId: string;
  readonly eventType:
    | "requested"
    | "accepted"
    | "started"
    | "ready"
    | "cancelled";
  readonly fromStatus: ProspectDemoLifecycleStatus | null;
  readonly toStatus: ProspectDemoLifecycleStatus;
  readonly creationPath: ProspectDemoCreationPath;
  readonly demoId: string | null;
  readonly demoUrl: string | null;
  readonly actor: string;
  readonly createdAt: string;
}

export interface ProspectDemoExecutionAttempt {
  readonly id: string;
  readonly requestId: string;
  readonly adapter: Exclude<ProspectDemoCreationPath, "undecided">;
  readonly outcome: "operator_required" | "materialized";
  readonly reasonCode: string | null;
  readonly detail: string;
  readonly sourceArtifactKind:
    | "reference-demo"
    | "solution-factory-draft"
    | "custom-target"
    | null;
  readonly sourceArtifactId: string | null;
  readonly targetUrl: string | null;
  readonly demoId: string | null;
  readonly demoUrl: string | null;
  readonly actor: string;
  readonly createdAt: string;
}

export interface ProspectDemoRequestDetail {
  readonly request: ProspectDemoRequestRecord;
  readonly events: readonly ProspectDemoRequestEvent[];
  readonly execution: {
    readonly latest: ProspectDemoExecutionAttempt | null;
    readonly attempts: readonly ProspectDemoExecutionAttempt[];
  };
}

export class PlatformApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
    readonly details?: Readonly<Record<string, unknown>>,
  ) {
    super(message);
    this.name = "PlatformApiError";
  }
}

type Fetcher = typeof fetch;

const REPUTATION_SEGMENT = /^[A-Za-z0-9._~-]+$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const YEAR_MONTH = /^\d{4}-\d{2}$/;
const SAFE_LOCATION_ID = /^[A-Za-z0-9._~-]{1,160}$/;
const TOPIC_SENTIMENT = new Set(["positivo", "neutral", "negativo"]);
const ORGANIZATION_ID = /^cd_org_[a-f0-9]{32}$/;
const SOLUTION_CERTIFICATION_ID = /^cd_sfc_[a-f0-9]{32}$/;
const SOLUTION_PROVISIONING_PLAN_ID = /^cd_sfp_[a-f0-9]{32}$/;
const SOLUTION_INSTANCE_ID = /^cd_sfi_[a-f0-9]{32}$/;
const CUSTOM_APP_ID = /^cd_capp_[a-f0-9]{32}$/;
const PROSPECT_DEMO_REQUEST_ID = /^cd_pdr_[a-f0-9]{32}$/;
const CUSTOM_SEGMENT = /^[a-z0-9](?:[a-z0-9-]{0,62})$/;

function uniqueSearchParams(query: string): URLSearchParams | null {
  const params = new URLSearchParams(query);
  const seen = new Set<string>();
  for (const [key] of params) {
    if (seen.has(key)) return null;
    seen.add(key);
  }
  return params;
}

function validReputationReportQuery(pathname: string, query: string): boolean {
  if (query === "") return true;
  const params = uniqueSearchParams(query);
  if (!params) return false;

  if (pathname === "/reputation-progress") {
    const allowed = new Set(["from", "to", "compare_from", "compare_to", "period", "location_id"]);
    for (const [key, value] of params) {
      if (!allowed.has(key)) return false;
      if (["from", "to", "compare_from", "compare_to"].includes(key) && !ISO_DATE.test(value)) return false;
      if (key === "period" && !YEAR_MONTH.test(value)) return false;
      if (key === "location_id" && !SAFE_LOCATION_ID.test(value)) return false;
    }
    return true;
  }

  if (/^\/reports\/topics\/[^/]+\/reviews$/.test(pathname)) {
    const allowed = new Set(["from", "to", "limit", "sentiment"]);
    for (const [key, value] of params) {
      if (!allowed.has(key)) return false;
      if (["from", "to"].includes(key) && !ISO_DATE.test(value)) return false;
      if (key === "limit" && (!/^\d{1,3}$/.test(value) || Number(value) < 1 || Number(value) > 100)) return false;
      if (key === "sentiment" && !TOPIC_SENTIMENT.has(value)) return false;
    }
    return true;
  }

  return false;
}

function validReputationPath(path: string): boolean {
  if (path.split("?").length > 2) return false;
  const [pathname = "", query = ""] = path.split("?", 2);

  const topicMatch = pathname.match(/^\/reports\/topics\/([^/]+)\/reviews$/);
  if (topicMatch) {
    const encodedTopic = topicMatch[1]!;
    if (/%(?:2f|5c)/i.test(encodedTopic)) return false;
    let decodedTopic: string;
    try {
      decodedTopic = decodeURIComponent(encodedTopic);
    } catch {
      return false;
    }
    if (!decodedTopic || decodedTopic === "." || decodedTopic === ".." || /[\\/]/.test(decodedTopic)) return false;
    return validReputationReportQuery(pathname, query);
  }

  const validPath = /^\/[A-Za-z0-9._~-]+(?:\/[A-Za-z0-9._~-]+)*$/.test(pathname)
    && !pathname.split("/").some((segment) => segment === "." || segment === "..");
  return validPath && validReputationReportQuery(pathname, query);
}

function base64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunk) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunk));
  }
  return btoa(binary);
}

export class PlatformApiClient {
  private csrfToken: string | null = null;
  private readonly fetcher: Fetcher;
  constructor(fetcher: Fetcher = fetch, private readonly basePath = "/api/v1/platform") {
    if (basePath !== "/api/v1/platform") throw new Error("platform_web_api_must_be_same_origin");
    this.fetcher = fetcher.bind(globalThis);
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const method = (init.method ?? "GET").toUpperCase();
    const headers = new Headers(init.headers);
    if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
      if (!this.csrfToken) throw new PlatformApiError(403, "csrf_required", "A fresh security token is required.");
      headers.set("x-csrf-token", this.csrfToken);
    }
    const response = await this.fetcher(`${this.basePath}${path}`, { ...init, method, credentials: "include", headers });
    if (response.status === 204) return undefined as T;
    const body = (await response.json()) as ErrorBody & { data?: T };
    if (!response.ok) {
      if (response.status === 401) this.csrfToken = null;
      throw new PlatformApiError(
        response.status,
        body.error?.code ?? "request_failed",
        body.error?.message ?? "The request could not be completed.",
        body.requestId,
        body.error?.details,
      );
    }
    return body.data as T;
  }

  currentSession(): Promise<{ user: PlatformUser; session: Session }> { return this.request("/auth/session"); }
  async refreshCsrf(): Promise<{ authenticated: boolean; csrfToken: string }> {
    const data = await this.request<{ authenticated: boolean; csrfToken: string }>("/auth/csrf"); this.csrfToken = data.csrfToken; return data;
  }
  async login(email: string, password: string): Promise<{ user: PlatformUser; session: Session }> {
    await this.refreshCsrf();
    const data = await this.request<{ user: PlatformUser; session: Session; csrfToken: string }>("/auth/login", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ email, password }) });
    this.csrfToken = data.csrfToken; return data;
  }
  async bootstrap(): Promise<{ session: { user: PlatformUser; session: Session }; context: ShellContext }> {
    const session = await this.currentSession(); await this.refreshCsrf(); const context = await this.request<ShellContext>("/me/context"); return { session, context };
  }
  context(): Promise<ShellContext> { return this.request("/me/context"); }
  resolveOrganization(slug: string): Promise<ResolvedOrganizationContext> { return this.request(`/me/organizations/resolve/${encodeURIComponent(slug)}`); }
  logout(): Promise<void> { return this.request("/auth/logout", { method: "POST" }); }
  logoutAll(): Promise<{ revoked: number }> { return this.request("/auth/logout-all", { method: "POST" }); }
  async changePassword(currentPassword: string, newPassword: string): Promise<{ user: PlatformUser; session: Session }> {
    const data = await this.request<{ user: PlatformUser; session: Session; csrfToken: string }>("/auth/change-password", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ currentPassword, newPassword }) });
    this.csrfToken = data.csrfToken; return data;
  }
  clearMemory(): void { this.csrfToken = null; }

  listCustomApps(organizationId: string): Promise<{ customApps: readonly CustomApp[] }> {
    if (!ORGANIZATION_ID.test(organizationId)) throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/custom-apps`);
  }

  resolveCustomApp(organizationId: string, appKey: string): Promise<{ customApp: CustomApp }> {
    if (!ORGANIZATION_ID.test(organizationId) || !CUSTOM_SEGMENT.test(appKey)) throw new PlatformApiError(400, "custom_app_path_invalid", "The custom app route is invalid.");
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/custom-apps/resolve/${encodeURIComponent(appKey)}`);
  }

  updateCustomApp(organizationId: string, customAppId: string, input: {
    displayName?: string;
    status?: string;
    publicSlug?: string | null;
    configuration?: Readonly<Record<string, unknown>>;
    expectedVersion: number;
  }): Promise<{ customApp: CustomApp; changed: boolean }> {
    if (!ORGANIZATION_ID.test(organizationId) || !CUSTOM_APP_ID.test(customAppId)) throw new PlatformApiError(400, "custom_app_path_invalid", "The custom app route is invalid.");
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/custom-apps/${encodeURIComponent(customAppId)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
  }

  customPublicToken(organizationId: string, customAppId: string): Promise<{ publicToken: CustomAppPublicToken | null }> {
    if (!ORGANIZATION_ID.test(organizationId) || !CUSTOM_APP_ID.test(customAppId)) throw new PlatformApiError(400, "custom_app_path_invalid", "The custom app route is invalid.");
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/custom-apps/${encodeURIComponent(customAppId)}/public-token`);
  }

  ensureCustomPublicToken(organizationId: string, customAppId: string, rotate = false): Promise<{ publicToken: CustomAppPublicToken }> {
    if (!ORGANIZATION_ID.test(organizationId) || !CUSTOM_APP_ID.test(customAppId)) throw new PlatformApiError(400, "custom_app_path_invalid", "The custom app route is invalid.");
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/custom-apps/${encodeURIComponent(customAppId)}/public-token`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ rotate }),
    });
  }

  async custom<T>(organizationSlug: string, path: string, init: RequestInit = {}): Promise<T> {
    const validPath = /^\/apps\/[a-z0-9](?:[a-z0-9-]{0,62})(?:\/assets)?$/.test(path);
    if (!CUSTOM_SEGMENT.test(organizationSlug) || !validPath) {
      throw new PlatformApiError(400, "custom_path_invalid", "The Custom route is invalid.");
    }
    const method = (init.method ?? "GET").toUpperCase();
    const headers = new Headers(init.headers);
    headers.set("x-clickdialog-organization-slug", organizationSlug);
    if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
      if (!this.csrfToken) throw new PlatformApiError(403, "csrf_required", "A fresh security token is required.");
      headers.set("x-csrf-token", this.csrfToken);
    }
    const response = await this.fetcher(`/api/v1/custom${path}`, { ...init, method, credentials: "include", headers });
    const body = response.status === 204 ? {} : await response.json().catch(() => ({})) as Record<string, unknown>;
    if (!response.ok) {
      if (response.status === 401) this.csrfToken = null;
      throw new PlatformApiError(response.status, typeof body.error === "string" ? body.error : `custom_${response.status}`, "Custom could not complete the request.", response.headers.get("x-request-id") ?? undefined);
    }
    return body as T;
  }

  async uploadCustomAsset(organizationSlug: string, appKey: string, file: File): Promise<CustomAsset> {
    if (!CUSTOM_SEGMENT.test(appKey)) throw new PlatformApiError(400, "custom_app_key_invalid", "The custom app key is invalid.");
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) throw new PlatformApiError(400, "custom_asset_type_invalid", "Use JPG, PNG or WebP images.");
    if (file.size < 1 || file.size > 2_000_000) throw new PlatformApiError(413, "custom_asset_too_large", "Images must be 2 MB or smaller.");
    const dataBase64 = base64(new Uint8Array(await file.arrayBuffer()));
    const result = await this.custom<{ asset: CustomAsset }>(organizationSlug, `/apps/${appKey}/assets`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ mimeType: file.type, dataBase64 }),
    });
    return result.asset;
  }

  async reputation<T>(organizationSlug: string, path: string, init: RequestInit = {}): Promise<T> {
    if (!/^[a-z0-9](?:[a-z0-9-]{0,62})$/.test(organizationSlug)) {
      throw new PlatformApiError(400, "organization_slug_invalid", "The organization route is invalid.");
    }
    if (!validReputationPath(path)) {
      throw new PlatformApiError(400, "reputation_path_invalid", "The Reputation route is invalid.");
    }
    const method = (init.method ?? "GET").toUpperCase();
    const headers = new Headers(init.headers);
    headers.set("x-clickdialog-organization-slug", organizationSlug);
    if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
      if (!this.csrfToken) throw new PlatformApiError(403, "csrf_required", "A fresh security token is required.");
      headers.set("x-csrf-token", this.csrfToken);
    }
    const response = await this.fetcher(`/api/v1/reputation${path}`, {
      ...init,
      method,
      credentials: "include",
      headers,
    });
    const body = response.status === 204 ? {} : await response.json().catch(() => ({})) as Record<string, unknown>;
    if (!response.ok) {
      if (response.status === 401) this.csrfToken = null;
      const nested = typeof body.error === "object" && body.error !== null ? body.error as Record<string, unknown> : null;
      const message = typeof body.error === "string" ? body.error : typeof nested?.message === "string" ? nested.message : "Reputation could not complete the request.";
      const code = typeof nested?.code === "string" ? nested.code : `reputation_${response.status}`;
      throw new PlatformApiError(response.status, code, message, typeof body.request_id === "string" ? body.request_id : response.headers.get("x-request-id") ?? undefined);
    }
    return body as T;
  }

  async menu<T>(organizationSlug: string, path: string, init: RequestInit = {}): Promise<T> {
    const [pathname, query = ""] = path.split("?", 2);
    const validPath = /^\/[A-Za-z0-9._~-]+(?:\/[A-Za-z0-9._~-]+)*$/.test(pathname ?? "")
      && !(pathname ?? "").split("/").some((segment) => segment === "." || segment === "..");
    const validQuery = query === "" || (pathname === "/agent/pending" && /^conversationId=[A-Za-z0-9._~%+-]+$/.test(query));
    if (!/^[a-z0-9](?:[a-z0-9-]{0,62})$/.test(organizationSlug) || !validPath || !validQuery) {
      throw new PlatformApiError(400, "menu_path_invalid", "The Menu route is invalid.");
    }
    const method = (init.method ?? "GET").toUpperCase();
    const headers = new Headers(init.headers);
    headers.set("x-clickdialog-organization-slug", organizationSlug);
    if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
      if (!this.csrfToken) throw new PlatformApiError(403, "csrf_required", "A fresh security token is required.");
      headers.set("x-csrf-token", this.csrfToken);
    }
    const response = await this.fetcher(`/api/v1/menu${path}`, { ...init, method, credentials: "include", headers });
    const body = response.status === 204 ? {} : await response.json().catch(() => ({})) as Record<string, unknown>;
    if (!response.ok) {
      if (response.status === 401) this.csrfToken = null;
      throw new PlatformApiError(response.status, typeof body.error === "string" ? body.error : `menu_${response.status}`, "Menu could not complete the request.", response.headers.get("x-request-id") ?? undefined);
    }
    return body as T;
  }

  salonsOverview(organizationId: string): Promise<SalonOverview> {
    if (!ORGANIZATION_ID.test(organizationId)) throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/salons/overview`);
  }

  salonAgenda(organizationId: string, from: string, to: string): Promise<SalonAgenda> {
    if (!ORGANIZATION_ID.test(organizationId)) throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    if (!Number.isFinite(Date.parse(from)) || !Number.isFinite(Date.parse(to))) {
      throw new PlatformApiError(400, "salons_agenda_range_invalid", "The agenda range is invalid.");
    }
    const params = new URLSearchParams({ from, to });
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/salons/agenda?${params.toString()}`);
  }


  salonInbox(
    organizationId: string,
    input: {
      q?: string;
      view?: SalonInboxView;
      scope?: SalonInboxScope;
      priority?: "all" | SalonConversationPriority;
      control?: "all" | SalonConversationControlMode;
      channel?: string;
      label?: string;
      limit?: number;
    } = {},
  ): Promise<SalonInbox> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    const q = input.q?.trim() ?? "";
    if (q.length > 160) throw new PlatformApiError(400, "salons_inbox_query_invalid", "The search query is too long.");
    const view = input.view ?? "all";
    const scope = input.scope ?? "all";
    const priority = input.priority ?? "all";
    const control = input.control ?? "all";
    const channel = input.channel?.trim().toLowerCase() || "all";
    const label = input.label?.trim() ?? "";
    if (!["all", "open", "attention", "ai", "booking", "closed"].includes(view)) {
      throw new PlatformApiError(400, "salons_inbox_view_invalid", "The inbox view is invalid.");
    }
    if (!["all", "mine", "unassigned"].includes(scope)) {
      throw new PlatformApiError(400, "salons_inbox_scope_invalid", "The inbox scope is invalid.");
    }
    if (!["all", "low", "medium", "high", "urgent"].includes(priority)) {
      throw new PlatformApiError(400, "salons_inbox_priority_invalid", "The priority filter is invalid.");
    }
    if (!["all", "ai", "human"].includes(control)) {
      throw new PlatformApiError(400, "salons_inbox_control_invalid", "The control filter is invalid.");
    }
    if (channel !== "all" && !/^[a-z0-9_-]{1,40}$/.test(channel)) {
      throw new PlatformApiError(400, "salons_inbox_channel_invalid", "The channel filter is invalid.");
    }
    if (label.length > 80) {
      throw new PlatformApiError(400, "salons_inbox_label_invalid", "The label filter is too long.");
    }
    const limit = input.limit ?? 50;
    if (!Number.isSafeInteger(limit) || limit < 1 || limit > 100) {
      throw new PlatformApiError(400, "salons_inbox_limit_invalid", "The inbox limit is invalid.");
    }
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    params.set("view", view);
    params.set("scope", scope);
    params.set("priority", priority);
    params.set("control", control);
    params.set("channel", channel);
    if (label) params.set("label", label);
    params.set("limit", String(limit));
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/salons/conversations?${params.toString()}`);
  }

  salonDemoSimulatorCatalog(organizationId: string): Promise<SalonDemoSimulatorCatalog> {
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/salons/conversations/simulator`);
  }
  startSalonDemoSimulation(organizationId: string, customerId: string): Promise<SalonDemoSimulationDetail> {
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/salons/conversations/simulator`, {
      method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ customerId }),
    });
  }
  salonDemoSimulationDetail(organizationId: string, conversationId: string): Promise<SalonDemoSimulationDetail> {
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/salons/conversations/simulator/${encodeURIComponent(conversationId)}`);
  }
  turnSalonDemoSimulation(organizationId: string, conversationId: string, input: {
    body: string; role: "customer" | "operator"; idempotencyKey: string;
  }): Promise<SalonDemoSimulationDetail> {
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/salons/conversations/simulator/${encodeURIComponent(conversationId)}/turn`, {
      method: "POST",
      headers: { "content-type": "application/json", "idempotency-key": input.idempotencyKey },
      body: JSON.stringify({ body: input.body, role: input.role }),
    });
  }

  salonConversation(organizationId: string, conversationId: string): Promise<SalonConversationDetail> {
    if (!ORGANIZATION_ID.test(organizationId) || !/^cd_cnv_[A-Za-z0-9._:-]+$/.test(conversationId)) {
      throw new PlatformApiError(400, "salons_conversation_invalid", "The conversation is invalid.");
    }
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/salons/conversations/${encodeURIComponent(conversationId)}`);
  }

  salonConversationWorkspace(
    organizationId: string,
  ): Promise<SalonConversationWorkspaceConfiguration> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/conversations/workspace`,
    );
  }

  mutateSalonConversation(
    organizationId: string,
    conversationId: string,
    input:
      | { type: "lifecycle"; lifecycle: SalonConversationLifecycle; expectedRevision: number }
      | { type: "priority"; priority: SalonConversationPriority; expectedRevision: number }
      | { type: "assignment"; assignedTo: string | null; assignedTeamId?: string | null; expectedRevision: number }
      | { type: "control"; controlMode: SalonConversationControlMode; expectedRevision: number }
      | { type: "labels"; labels: readonly string[]; expectedRevision: number },
  ): Promise<{ state: SalonConversationOperatorState; idempotent: boolean; changed: boolean }> {
    if (!ORGANIZATION_ID.test(organizationId) || !/^cd_cnv_[A-Za-z0-9._:-]+$/.test(conversationId)) {
      throw new PlatformApiError(400, "salons_conversation_invalid", "The conversation is invalid.");
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/conversations/${encodeURIComponent(conversationId)}/operator`,
      {
        method: "PATCH",
        headers: {
          "content-type": "application/json",
          "idempotency-key": `salons-conversation-mutate-${crypto.randomUUID()}`,
        },
        body: JSON.stringify(input),
      },
    );
  }

  addSalonConversationPrivateNote(
    organizationId: string,
    conversationId: string,
    body: string,
    expectedRevision: number,
  ): Promise<{ state: SalonConversationOperatorState; idempotent: boolean }> {
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/conversations/${encodeURIComponent(conversationId)}/notes`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": `salons-conversation-note-${crypto.randomUUID()}`,
        },
        body: JSON.stringify({ body, expectedRevision }),
      },
    );
  }

  replySalonConversation(
    organizationId: string,
    conversationId: string,
    body: string,
  ): Promise<SalonConversationDetail["outbound"][number]> {
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/conversations/${encodeURIComponent(conversationId)}/reply`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": `salons-conversation-reply-${crypto.randomUUID()}`,
        },
        body: JSON.stringify({ body }),
      },
    );
  }

  createSalonConversationCannedResponse(
    organizationId: string,
    input: { title: string; shortcut: string; body: string },
  ): Promise<{ response: SalonConversationCannedResponse; idempotent: boolean }> {
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/conversations/canned-responses`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": `salons-canned-response-${crypto.randomUUID()}`,
        },
        body: JSON.stringify(input),
      },
    );
  }

  updateSalonConversationCannedResponse(
    organizationId: string,
    responseId: string,
    input: Partial<Pick<SalonConversationCannedResponse, "title" | "shortcut" | "body" | "active">> & { expectedVersion: number },
  ): Promise<{ response: SalonConversationCannedResponse; changed: boolean }> {
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/conversations/canned-responses/${encodeURIComponent(responseId)}`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    );
  }

  salonCustomers(
    organizationId: string,
    input: {
      q?: string;
      segment?: SalonCustomerSegment;
      sort?: SalonCustomerSort;
      activity?: "all" | "with_activity" | "without_activity";
      upcoming?: "all" | "yes" | "no";
      history?: "all" | "yes" | "no";
      noShow?: "all" | "yes" | "no";
      serviceId?: string | null;
      professionalId?: string | null;
      locationId?: string | null;
      channel?: string | null;
      tag?: string | null;
      limit?: number;
    } = {},
  ): Promise<SalonCustomerList> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    const q = input.q?.trim() ?? "";
    if (q.length > 160) throw new PlatformApiError(400, "salons_customer_query_invalid", "The customer search is too long.");
    const segment = input.segment ?? "all";
    const sort = input.sort ?? "activity_desc";
    const activity = input.activity ?? "all";
    const upcoming = input.upcoming ?? "all";
    const history = input.history ?? "all";
    const noShow = input.noShow ?? "all";
    const limit = input.limit ?? 50;
    if (!["all", "recurrent", "recover"].includes(segment)) {
      throw new PlatformApiError(400, "salons_customer_segment_invalid", "The customer segment is invalid.");
    }
    if (!["activity_desc", "activity_asc", "next_booking_asc", "name_asc", "spend_desc", "spend_asc"].includes(sort)) {
      throw new PlatformApiError(400, "salons_customer_sort_invalid", "The customer sort is invalid.");
    }
    if (!["all", "with_activity", "without_activity"].includes(activity)) {
      throw new PlatformApiError(400, "salons_customer_filter_invalid", "The activity filter is invalid.");
    }
    for (const value of [upcoming, history, noShow]) {
      if (!["all", "yes", "no"].includes(value)) {
        throw new PlatformApiError(400, "salons_customer_filter_invalid", "A customer filter is invalid.");
      }
    }
    if (!Number.isSafeInteger(limit) || limit < 1 || limit > 100) {
      throw new PlatformApiError(400, "salons_customer_limit_invalid", "The customer limit is invalid.");
    }
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    params.set("segment", segment);
    params.set("sort", sort);
    if (activity !== "all") params.set("activity", activity);
    if (upcoming !== "all") params.set("upcoming", upcoming);
    if (history !== "all") params.set("history", history);
    if (noShow !== "all") params.set("noShow", noShow);
    if (input.serviceId) params.set("serviceId", input.serviceId);
    if (input.professionalId) params.set("professionalId", input.professionalId);
    if (input.locationId) params.set("locationId", input.locationId);
    if (input.channel) params.set("channel", input.channel);
    if (input.tag) params.set("tag", input.tag);
    params.set("limit", String(limit));
    return this.request(
      "/organizations/" + encodeURIComponent(organizationId) + "/salons/customers?" + params.toString(),
    );
  }

  salonCustomer360(organizationId: string, customerId: string): Promise<SalonCustomer360> {
    if (!ORGANIZATION_ID.test(organizationId) || !/^cd_cus_[A-Za-z0-9._:-]+$/.test(customerId)) {
      throw new PlatformApiError(400, "salons_customer_invalid", "The customer is invalid.");
    }
    return this.request(
      "/organizations/" + encodeURIComponent(organizationId) + "/salons/customers/" + encodeURIComponent(customerId),
    );
  }

  createSalonCustomer(
    organizationId: string,
    input: {
      displayName?: string | null;
      phone?: string | null;
      email?: string | null;
      notes?: string | null;
      tags?: readonly string[];
      preferences?: Readonly<Record<string, string | number | boolean | null>>;
    },
  ): Promise<SalonCustomerMutationResult> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    return this.request(
      "/organizations/" + encodeURIComponent(organizationId) + "/salons/customers",
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": "salons-web-customer-create-" + crypto.randomUUID(),
        },
        body: JSON.stringify(input),
      },
    );
  }

  updateSalonCustomer(
    organizationId: string,
    customerId: string,
    input: {
      expectedRevision: number;
      displayName?: string;
      phone?: string;
      email?: string | null;
      notes?: string | null;
      tags?: readonly string[];
      preferences?: Readonly<Record<string, string | number | boolean | null>>;
    },
  ): Promise<SalonCustomerMutationResult> {
    if (!ORGANIZATION_ID.test(organizationId) || !/^cd_cus_[A-Za-z0-9._:-]+$/.test(customerId)) {
      throw new PlatformApiError(400, "salons_customer_invalid", "The customer is invalid.");
    }
    return this.request(
      "/organizations/" + encodeURIComponent(organizationId) + "/salons/customers/" + encodeURIComponent(customerId),
      {
        method: "PATCH",
        headers: {
          "content-type": "application/json",
          "idempotency-key": "salons-web-customer-update-" + crypto.randomUUID(),
        },
        body: JSON.stringify(input),
      },
    );
  }

  archiveSalonCustomer(
    organizationId: string,
    customerId: string,
    expectedRevision: number,
  ): Promise<SalonCustomerMutationResult> {
    if (!ORGANIZATION_ID.test(organizationId) || !/^cd_cus_[A-Za-z0-9._:-]+$/.test(customerId)) {
      throw new PlatformApiError(400, "salons_customer_invalid", "The customer is invalid.");
    }
    return this.request(
      "/organizations/" + encodeURIComponent(organizationId) + "/salons/customers/" + encodeURIComponent(customerId) + "/archive",
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": "salons-web-customer-archive-" + crypto.randomUUID(),
        },
        body: JSON.stringify({ expectedRevision }),
      },
    );
  }

  mergeSalonCustomer(
    organizationId: string,
    sourceCustomerId: string,
    input: {
      targetCustomerId: string;
      expectedSourceRevision: number;
      expectedTargetRevision: number;
    },
  ): Promise<SalonCustomerMutationResult> {
    if (
      !ORGANIZATION_ID.test(organizationId) ||
      !/^cd_cus_[A-Za-z0-9._:-]+$/.test(sourceCustomerId) ||
      !/^cd_cus_[A-Za-z0-9._:-]+$/.test(input.targetCustomerId)
    ) {
      throw new PlatformApiError(400, "salons_customer_invalid", "The customer merge is invalid.");
    }
    return this.request(
      "/organizations/" + encodeURIComponent(organizationId) + "/salons/customers/" + encodeURIComponent(sourceCustomerId) + "/merge",
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": "salons-web-customer-merge-" + crypto.randomUUID(),
        },
        body: JSON.stringify(input),
      },
    );
  }

  previewSalonCustomerImport(
    organizationId: string,
    input: { fileName: string; contentBase64: string; mapping?: SalonCustomerImportMapping },
  ): Promise<SalonCustomerImportPreview> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    return this.request(
      "/organizations/" + encodeURIComponent(organizationId) + "/salons/customers/import/preview",
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    );
  }

  commitSalonCustomerImport(
    organizationId: string,
    input: {
      fileName: string;
      contentBase64: string;
      mapping: SalonCustomerImportMapping;
      decisions: readonly {
        rowNumber: number;
        action: "create" | "skip" | "update";
        targetCustomerId?: string | null;
      }[];
    },
  ): Promise<SalonCustomerImportCommitResult> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    return this.request(
      "/organizations/" + encodeURIComponent(organizationId) + "/salons/customers/import/commit",
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": "salons-web-customer-import-" + crypto.randomUUID(),
        },
        body: JSON.stringify(input),
      },
    );
  }

  exportSalonCustomers(organizationId: string): Promise<SalonCustomerExport> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    return this.request(
      "/organizations/" + encodeURIComponent(organizationId) + "/salons/customers/export",
    );
  }

  salonReports(
    organizationId: string,
    from: string,
    to: string,
    limit = 20,
    filters: {
      locationId?: string | null;
      professionalId?: string | null;
      serviceId?: string | null;
      source?: string | null;
    } = {},
  ): Promise<SalonReports> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    if (!Number.isFinite(Date.parse(from)) || !Number.isFinite(Date.parse(to))) {
      throw new PlatformApiError(400, "salons_reports_range_invalid", "The reports range is invalid.");
    }
    if (!Number.isSafeInteger(limit) || limit < 1 || limit > 100) {
      throw new PlatformApiError(400, "salons_reports_limit_invalid", "The reports limit is invalid.");
    }
    const params = new URLSearchParams({ from, to, limit: String(limit) });
    if (filters.locationId) params.set("locationId", filters.locationId);
    if (filters.professionalId) params.set("professionalId", filters.professionalId);
    if (filters.serviceId) params.set("serviceId", filters.serviceId);
    if (filters.source) params.set("source", filters.source);
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/reports?${params.toString()}`,
    );
  }

  salonSales(
    organizationId: string,
    from: string,
    to: string,
    limit = 500,
  ): Promise<SalonSalesScreen> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    if (!Number.isFinite(Date.parse(from)) || !Number.isFinite(Date.parse(to))) {
      throw new PlatformApiError(400, "salons_sales_range_invalid", "The sales range is invalid.");
    }
    if (!Number.isSafeInteger(limit) || limit < 1 || limit > 1000) {
      throw new PlatformApiError(400, "salons_sales_limit_invalid", "The sales limit is invalid.");
    }
    const params = new URLSearchParams({ from, to, limit: String(limit) });
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/sales?${params.toString()}`,
    );
  }

  createSalonSale(
    organizationId: string,
    input: {
      appointmentId: string;
      estimatedAmountMinor?: number;
      currency?: string;
      notes?: string | null;
      discount?: {
        type: "fixed" | "percentage";
        value: number;
        reason: string;
      } | null;
    },
  ): Promise<{ created: boolean; sale: SalonSale }> {
    if (!ORGANIZATION_ID.test(organizationId) || !/^cd_apt_[A-Za-z0-9._:-]+$/.test(input.appointmentId)) {
      throw new PlatformApiError(400, "salons_sale_invalid", "The sale is invalid.");
    }
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/salons/sales`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "idempotency-key": `salons-web-sale-${crypto.randomUUID()}`,
      },
      body: JSON.stringify(input),
    });
  }

  createManualSalonSale(
    organizationId: string,
    input: {
      customerId?: string | null;
      locationId?: string | null;
      currency?: string;
      source?: string;
      notes?: string | null;
      lines: readonly {
        kind: "service" | "custom";
        serviceId?: string;
        description?: string;
        quantity?: number;
        unitPriceMinor?: number;
        currency?: string;
        resourceId?: string | null;
        locationId?: string | null;
        discount?: {
          type: "fixed" | "percentage";
          value: number;
          reason: string;
        } | null;
      }[];
      discount?: {
        type: "fixed" | "percentage";
        value: number;
        reason: string;
      } | null;
    },
  ): Promise<{ created: boolean; sale: SalonSale }> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/sales/manual`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": `salons-web-manual-sale-${crypto.randomUUID()}`,
        },
        body: JSON.stringify(input),
      },
    );
  }

  createSalonSaleCollection(
    organizationId: string,
    saleId: string,
    input: {
      kind: "manual" | "provider";
      method: "cash" | "bank_transfer" | "external_card" | "other" | "mercadopago";
      amountMinor: number;
      notes?: string | null;
      expectedVersion: number;
    },
  ): Promise<{
    replayed: boolean;
    sale: SalonSale;
    collection: SalonSaleCollection;
    checkout: SalonPaymentCheckout | null;
  }> {
    if (
      !ORGANIZATION_ID.test(organizationId) ||
      !/^cd_sale_[A-Za-z0-9._:-]+$/.test(saleId)
    ) {
      throw new PlatformApiError(400, "salons_sale_invalid", "The sale is invalid.");
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/sales/${encodeURIComponent(saleId)}/collections`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": `salons-web-collection-${crypto.randomUUID()}`,
        },
        body: JSON.stringify(input),
      },
    );
  }

  refundSalonSaleCollection(
    organizationId: string,
    saleId: string,
    collectionId: string,
    input: {
      amountMinor?: number | null;
      expectedVersion: number;
    },
  ): Promise<{
    sale: SalonSale;
    collection: SalonSaleCollection;
    refund: {
      refundId: string;
      paymentId: string;
      provider: string;
      providerRefundId: string;
      amountMinor: number;
      currency: string;
      status: "pending" | "approved" | "rejected";
      createdAt: string;
      updatedAt: string;
    };
  }> {
    if (
      !ORGANIZATION_ID.test(organizationId) ||
      !/^cd_sale_[A-Za-z0-9._:-]+$/.test(saleId) ||
      !/^cd_col_[A-Za-z0-9._:-]+$/.test(collectionId)
    ) {
      throw new PlatformApiError(400, "salons_sale_invalid", "The Sale collection is invalid.");
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/sales/${encodeURIComponent(saleId)}/collections/${encodeURIComponent(collectionId)}/refund`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    );
  }

  salonSaleReceipt(
    organizationId: string,
    saleId: string,
  ): Promise<SalonSaleReceipt> {
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/sales/${encodeURIComponent(saleId)}/receipt`,
    );
  }

  salonMercadoPagoConnection(
    organizationId: string,
  ): Promise<SalonMercadoPagoConnection> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/payments/mercadopago`,
    );
  }

  startSalonMercadoPagoOauth(
    organizationId: string,
  ): Promise<{
    provider: "mercadopago";
    authorizationUrl: string;
    expiresAt: string;
  }> {
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/payments/mercadopago/oauth/start`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: "{}",
      },
    );
  }

  disconnectSalonMercadoPago(
    organizationId: string,
  ): Promise<{ disconnected: boolean; merchantConnectionId: string | null }> {
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/payments/mercadopago`,
      { method: "DELETE" },
    );
  }

  createSalonAppointmentDepositCheckout(
    organizationId: string,
    appointmentId: string,
  ): Promise<{
    replayed: boolean;
    deposit: SalonAppointmentDeposit;
    checkout: SalonPaymentCheckout;
  }> {
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/agenda/${encodeURIComponent(appointmentId)}/deposit/checkout`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": `salons-web-deposit-${appointmentId}`,
        },
        body: "{}",
      },
    );
  }

  salonAppointmentDeposit(
    organizationId: string,
    appointmentId: string,
  ): Promise<SalonAppointmentDeposit | null> {
    return this.request<{ deposit: SalonAppointmentDeposit | null }>(
      `/organizations/${encodeURIComponent(organizationId)}/salons/agenda/${encodeURIComponent(appointmentId)}/deposit`,
    ).then((payload) => payload.deposit);
  }

  collectSalonSale(
    organizationId: string,
    saleId: string,
    input: {
      mode: "total" | "partial" | "courtesy";
      amountMinor?: number;
      paymentMethod?: "cash" | "transfer" | "card" | "mercadopago" | "other";
      notes?: string | null;
      expectedVersion: number;
    },
  ): Promise<SalonSale> {
    if (!ORGANIZATION_ID.test(organizationId) || !/^cd_sale_[A-Za-z0-9._:-]+$/.test(saleId)) {
      throw new PlatformApiError(400, "salons_sale_invalid", "The sale is invalid.");
    }
    return this.request<{ sale: SalonSale }>(`/organizations/${encodeURIComponent(organizationId)}/salons/sales/${encodeURIComponent(saleId)}/collect`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    }).then((payload) => payload.sale);
  }

  editSalonSale(
    organizationId: string,
    saleId: string,
    input: {
      collectedAmountMinor: number;
      paymentMethod: "unknown" | "cash" | "transfer" | "card" | "mercadopago" | "other";
      courtesy?: boolean;
      notes?: string | null;
      expectedVersion: number;
    },
  ): Promise<SalonSale> {
    if (!ORGANIZATION_ID.test(organizationId) || !/^cd_sale_[A-Za-z0-9._:-]+$/.test(saleId)) {
      throw new PlatformApiError(400, "salons_sale_invalid", "The sale is invalid.");
    }
    return this.request<{ sale: SalonSale }>(`/organizations/${encodeURIComponent(organizationId)}/salons/sales/${encodeURIComponent(saleId)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    }).then((payload: { sale: SalonSale }) => payload.sale);
  }

  cancelSalonSale(
    organizationId: string,
    saleId: string,
    input: { reason: string; expectedVersion: number },
  ): Promise<SalonSale> {
    if (!ORGANIZATION_ID.test(organizationId) || !/^cd_sale_[A-Za-z0-9._:-]+$/.test(saleId)) {
      throw new PlatformApiError(400, "salons_sale_invalid", "The sale is invalid.");
    }
    return this.request<{ sale: SalonSale }>(`/organizations/${encodeURIComponent(organizationId)}/salons/sales/${encodeURIComponent(saleId)}/cancel`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    }).then((payload: { sale: SalonSale }) => payload.sale);
  }

  salonMetaWhatsAppConnections(organizationId: string): Promise<SalonMetaWhatsAppConnections> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/integrations/meta/whatsapp/connections`,
    );
  }

  async updateSalonService(
    organizationId: string,
    serviceId: string,
    input: {
      name: string;
      durationMinutes: number;
      active: boolean;
      description: string | null;
      expectedVersion: number;
    },
  ): Promise<{ service: SalonService }> {
    if (!ORGANIZATION_ID.test(organizationId) || !/^cd_svc_[A-Za-z0-9._:-]+$/.test(serviceId)) {
      throw new PlatformApiError(400, "salons_service_invalid", "The service is invalid.");
    }
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/salons/services/${encodeURIComponent(serviceId)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
  }

  salonCalComConnection(
    organizationId: string,
  ): Promise<SalonCalComConnection | null> {
    return this.request<{ connection: SalonCalComConnection | null }>(
      `/organizations/${encodeURIComponent(organizationId)}/scheduling/calcom`,
    ).then((payload) => payload.connection);
  }

  startSalonCalComOAuth(
    organizationId: string,
    returnPath: string,
  ): Promise<{ authorizationUrl: string; expiresAt: string }> {
    return this.request<{ authorizationUrl: string; expiresAt: string }>(
      `/organizations/${encodeURIComponent(organizationId)}/scheduling/calcom/oauth/start`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ returnPath }),
      },
    );
  }

  validateSalonCalComConnection(
    organizationId: string,
  ): Promise<{ connection: SalonCalComConnection; eventTypes: readonly SalonCalComEventType[] }> {
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/scheduling/calcom/validate`,
      { method: "POST", headers: { "content-type": "application/json" }, body: "{}" },
    );
  }

  disconnectSalonCalCom(
    organizationId: string,
  ): Promise<SalonCalComConnection> {
    return this.request<{ connection: SalonCalComConnection }>(
      `/organizations/${encodeURIComponent(organizationId)}/scheduling/calcom`,
      { method: "DELETE" },
    ).then((payload) => payload.connection);
  }

  salonCalComEventTypes(
    organizationId: string,
  ): Promise<readonly SalonCalComEventType[]> {
    return this.request<{ eventTypes: readonly SalonCalComEventType[] }>(
      `/organizations/${encodeURIComponent(organizationId)}/scheduling/calcom/event-types`,
    ).then((payload) => payload.eventTypes);
  }

  salonCalComServiceBindings(
    organizationId: string,
  ): Promise<readonly SalonCalComServiceBinding[]> {
    return this.request<{ bindings: readonly SalonCalComServiceBinding[] }>(
      `/organizations/${encodeURIComponent(organizationId)}/scheduling/calcom/service-bindings`,
    ).then((payload) => payload.bindings);
  }

  bindSalonServiceToCalCom(
    organizationId: string,
    serviceId: string,
    externalEventTypeId: string,
  ): Promise<SalonCalComServiceBinding> {
    return this.request<{ binding: SalonCalComServiceBinding }>(
      `/organizations/${encodeURIComponent(organizationId)}/scheduling/calcom/service-bindings/${encodeURIComponent(serviceId)}`,
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ externalEventTypeId }),
      },
    ).then((payload) => payload.binding);
  }

  unbindSalonServiceFromCalCom(
    organizationId: string,
    serviceId: string,
  ): Promise<SalonCalComServiceBinding> {
    return this.request<{ binding: SalonCalComServiceBinding }>(
      `/organizations/${encodeURIComponent(organizationId)}/scheduling/calcom/service-bindings/${encodeURIComponent(serviceId)}`,
      { method: "DELETE" },
    ).then((payload) => payload.binding);
  }

  salonSchedulingSettings(organizationId: string): Promise<SalonSchedulingSettings> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/scheduling`,
    );
  }

  salonAutomationSettings(organizationId: string): Promise<SalonAutomationSettings> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/automations`,
    );
  }

  updateSalonAutomationRule(
    organizationId: string,
    input: SalonAutomationRule,
  ): Promise<{ rule: SalonAutomationRule }> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/automations`,
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    );
  }

  salonKnowledgeSettings(organizationId: string): Promise<SalonKnowledgeSettings> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/knowledge`,
    );
  }

  createSalonKnowledgeDocument(
    organizationId: string,
    input: {
      title: string;
      text: string;
      status: "draft" | "approved";
    },
  ): Promise<{
    source: SalonKnowledgeSource;
    document: unknown;
    replayed: boolean;
  }> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/knowledge/documents`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": `salons-knowledge-${crypto.randomUUID()}`,
        },
        body: JSON.stringify(input),
      },
    );
  }

  updateSalonKnowledgeSource(
    organizationId: string,
    sourceId: string,
    input: {
      status: "draft" | "approved" | "archived";
      expectedVersion: number;
    },
  ): Promise<{ source: SalonKnowledgeSource }> {
    if (
      !ORGANIZATION_ID.test(organizationId) ||
      !/^cd_ksrc_[0-9a-f]{32}$/.test(sourceId)
    ) {
      throw new PlatformApiError(400, "salons_knowledge_source_invalid", "The Knowledge source is invalid.");
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/knowledge/sources/${encodeURIComponent(sourceId)}`,
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    );
  }

  querySalonKnowledge(
    organizationId: string,
    queryText: string,
  ): Promise<{ result: SalonKnowledgeQueryResult }> {
    if (!ORGANIZATION_ID.test(organizationId) || queryText.trim().length === 0) {
      throw new PlatformApiError(400, "salons_knowledge_query_invalid", "The Knowledge query is invalid.");
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/knowledge/query`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ queryText }),
      },
    );
  }

  salonAssistantReadiness(organizationId: string): Promise<SalonAssistantReadiness> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/assistant`,
    );
  }

  updateSalonAssistantSettings(
    organizationId: string,
    input: Omit<SalonAssistantProductSettings, "persisted" | "version" | "updatedAt" | "updatedBy"> & {
      expectedVersion: number;
    },
  ): Promise<{ settings: SalonAssistantProductSettings }> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/assistant`,
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    );
  }

  updateSalonBusiness(
    organizationId: string,
    input: Partial<Omit<SalonSchedulingBusiness, "version">> & { expectedVersion: number },
  ): Promise<SalonSchedulingBusiness> {
    return this.request<{ business: SalonSchedulingBusiness }>(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/business`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    ).then((payload) => payload.business);
  }

  createSalonLocation(
    organizationId: string,
    input: {
      name: string;
      address?: string | null;
      phone?: string | null;
      email?: string | null;
      timezone?: string;
      active?: boolean;
      onlineBookingEnabled?: boolean;
    },
  ): Promise<SalonSchedulingLocation> {
    return this.request<{ location: SalonSchedulingLocation }>(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/locations`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    ).then((payload) => payload.location);
  }

  updateSalonLocation(
    organizationId: string,
    locationId: string,
    input: Partial<Omit<SalonSchedulingLocation, "id" | "version">> & { expectedVersion: number },
  ): Promise<SalonSchedulingLocation> {
    return this.request<{ location: SalonSchedulingLocation }>(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/locations/${encodeURIComponent(locationId)}`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    ).then((payload) => payload.location);
  }

  createSalonServiceCategory(
    organizationId: string,
    input: { name: string; active?: boolean; sortOrder?: number },
  ): Promise<SalonServiceCategory> {
    return this.request<{ category: SalonServiceCategory }>(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/categories`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    ).then((payload) => payload.category);
  }

  updateSalonServiceCategory(
    organizationId: string,
    categoryId: string,
    input: Partial<Omit<SalonServiceCategory, "id" | "version">> & { expectedVersion: number },
  ): Promise<SalonServiceCategory> {
    return this.request<{ category: SalonServiceCategory }>(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/categories/${encodeURIComponent(categoryId)}`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    ).then((payload) => payload.category);
  }

  createSalonSchedulingResource(
    organizationId: string,
    input: {
      name: string;
      displayName?: string | null;
      resourceType: SalonSchedulingResourceType;
      active?: boolean;
      linkedUserId?: string | null;
      locationIds?: readonly string[];
      capabilities?: readonly string[];
    },
  ): Promise<SalonSchedulingResource> {
    return this.request<{ resource: SalonSchedulingResource }>(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/resources`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    ).then((payload) => payload.resource);
  }

  updateSalonSchedulingResource(
    organizationId: string,
    resourceId: string,
    input: Partial<Omit<SalonSchedulingResource, "id" | "version">> & { expectedVersion: number },
  ): Promise<SalonSchedulingResource> {
    return this.request<{ resource: SalonSchedulingResource }>(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/resources/${encodeURIComponent(resourceId)}`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    ).then((payload) => payload.resource);
  }

  createSalonAvailabilityRule(
    organizationId: string,
    input: Omit<SalonAvailabilityRule, "id" | "version">,
  ): Promise<SalonAvailabilityRule> {
    return this.request<{ rule: SalonAvailabilityRule }>(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/availability-rules`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    ).then((payload) => payload.rule);
  }

  updateSalonAvailabilityRule(
    organizationId: string,
    ruleId: string,
    input: Partial<Omit<SalonAvailabilityRule, "id" | "version">> & { expectedVersion: number },
  ): Promise<SalonAvailabilityRule> {
    return this.request<{ rule: SalonAvailabilityRule }>(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/availability-rules/${encodeURIComponent(ruleId)}`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    ).then((payload) => payload.rule);
  }

  createSalonAvailabilityBlock(
    organizationId: string,
    input: Omit<SalonAvailabilityBlock, "id" | "version">,
  ): Promise<SalonAvailabilityBlock> {
    return this.request<{ block: SalonAvailabilityBlock }>(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/availability-blocks`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    ).then((payload) => payload.block);
  }

  updateSalonAvailabilityBlock(
    organizationId: string,
    blockId: string,
    input: Partial<Omit<SalonAvailabilityBlock, "id" | "version">> & { expectedVersion: number },
  ): Promise<SalonAvailabilityBlock> {
    return this.request<{ block: SalonAvailabilityBlock }>(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/availability-blocks/${encodeURIComponent(blockId)}`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    ).then((payload) => payload.block);
  }

  createSalonServiceConfig(
    organizationId: string,
    input: {
      name: string;
      durationMinutes: number;
      bufferBeforeMinutes?: number;
      bufferAfterMinutes?: number;
      priceMinor?: number | null;
      currency?: string;
      active?: boolean;
      description?: string | null;
      publicBookingEnabled?: boolean;
      categoryId?: string | null;
      locationIds?: readonly string[];
      eligibleResourceIds?: readonly string[];
      resourceRequirements?: readonly SalonServiceResourceRequirement[];
    },
  ): Promise<SalonSchedulingServiceConfig> {
    return this.request<{ service: SalonSchedulingServiceConfig }>(
      `/organizations/${encodeURIComponent(organizationId)}/salons/services`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    ).then((payload) => payload.service);
  }

  updateSalonServiceConfig(
    organizationId: string,
    serviceId: string,
    input: Partial<Omit<SalonSchedulingServiceConfig, "id" | "version">> & { expectedVersion: number },
  ): Promise<SalonSchedulingServiceConfig> {
    return this.request<{ service: SalonSchedulingServiceConfig }>(
      `/organizations/${encodeURIComponent(organizationId)}/salons/services/${encodeURIComponent(serviceId)}`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    ).then((payload) => payload.service);
  }

  updateSalonBookingRules(
    organizationId: string,
    input: Partial<Omit<SalonBookingRules, "version">> & { expectedVersion: number },
  ): Promise<SalonBookingRules> {
    return this.request<{ bookingRules: SalonBookingRules }>(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/booking-rules`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    ).then((payload) => payload.bookingRules);
  }

  updateSalonPublicBooking(
    organizationId: string,
    input: Partial<Omit<SalonPublicBookingSettings, "version">> & { expectedVersion: number },
  ): Promise<SalonPublicBookingSettings> {
    return this.request<{ publicBooking: SalonPublicBookingSettings }>(
      `/organizations/${encodeURIComponent(organizationId)}/salons/settings/public-booking`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    ).then((payload) => payload.publicBooking);
  }

  salonSchedulingAvailability(
    organizationId: string,
    input: {
      serviceId: string;
      startsAfter: string;
      endsBefore: string;
      timeZone: string;
      locationId?: string | null;
      resourceId?: string | null;
      preferredResourceIds?: readonly string[];
      appointmentIdToReschedule?: string | null;
    },
  ): Promise<readonly SalonAvailabilitySlot[]> {
    if (
      !ORGANIZATION_ID.test(organizationId) ||
      !/^cd_svc_[A-Za-z0-9._:-]+$/.test(input.serviceId)
    ) {
      throw new PlatformApiError(
        400,
        "salons_availability_invalid",
        "The availability query is invalid.",
      );
    }
    return this.request<{ slots: readonly SalonAvailabilitySlot[] }>(
      `/organizations/${encodeURIComponent(organizationId)}/scheduling/availability/query`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    ).then((payload) => payload.slots);
  }

  createSalonAgendaBlocks(
    organizationId: string,
    input: {
      locationId: string | null;
      resourceIds: readonly string[];
      startsAt: string;
      endsAt: string;
      reason: string | null;
      visibleLabel: string | null;
    },
  ): Promise<{
    blocks: readonly SalonAvailabilityBlock[];
    overlapPolicy: "allowed_union";
  }> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(
        400,
        "salons_appointment_invalid",
        "The organization is invalid.",
      );
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/agenda/blocks`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    );
  }

  createSalonAgendaAppointment(
    organizationId: string,
    input: {
      serviceId: string;
      customerId: string | null;
      locationId: string | null;
      resourceId: string | null;
      preferredResourceIds?: readonly string[];
      startsAt: string;
      timeZone: string;
    },
  ): Promise<{ appointment: { id: string; status: SalonAppointmentStatus; startsAt: string; endsAt: string }; idempotent: boolean }> {
    if (
      !ORGANIZATION_ID.test(organizationId) ||
      !/^cd_svc_[A-Za-z0-9._:-]+$/.test(input.serviceId)
    ) {
      throw new PlatformApiError(
        400,
        "salons_appointment_invalid",
        "The appointment is invalid.",
      );
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/agenda/appointments`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": `salons-agenda-create-${crypto.randomUUID()}`,
        },
        body: JSON.stringify(input),
      },
    );
  }

  rescheduleSalonAgendaAppointment(
    organizationId: string,
    appointmentId: string,
    input: {
      startsAt: string;
      timeZone: string;
      preferredResourceIds?: readonly string[];
    },
  ): Promise<{ appointment: { id: string; status: SalonAppointmentStatus; startsAt: string; endsAt: string }; idempotent: boolean }> {
    if (
      !ORGANIZATION_ID.test(organizationId) ||
      !/^cd_apt_[A-Za-z0-9._:-]+$/.test(appointmentId)
    ) {
      throw new PlatformApiError(
        400,
        "salons_appointment_invalid",
        "The appointment is invalid.",
      );
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/agenda/appointments/${encodeURIComponent(appointmentId)}/reschedule`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": `salons-agenda-reschedule-${crypto.randomUUID()}`,
        },
        body: JSON.stringify(input),
      },
    );
  }

  cancelSalonAgendaAppointment(
    organizationId: string,
    appointmentId: string,
  ): Promise<{ appointment: { id: string; status: SalonAppointmentStatus }; idempotent: boolean }> {
    if (
      !ORGANIZATION_ID.test(organizationId) ||
      !/^cd_apt_[A-Za-z0-9._:-]+$/.test(appointmentId)
    ) {
      throw new PlatformApiError(
        400,
        "salons_appointment_invalid",
        "The appointment is invalid.",
      );
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/agenda/appointments/${encodeURIComponent(appointmentId)}/cancel`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": `salons-agenda-cancel-${crypto.randomUUID()}`,
        },
        body: "{}",
      },
    );
  }

  updateSalonAgendaAppointmentNotes(
    organizationId: string,
    appointmentId: string,
    input: { notes: string; expectedRevision: number },
  ): Promise<{ notes: string; revision: number; updatedAt: string }> {
    if (
      !ORGANIZATION_ID.test(organizationId) ||
      !/^cd_apt_[A-Za-z0-9._:-]+$/.test(appointmentId)
    ) {
      throw new PlatformApiError(
        400,
        "salons_appointment_invalid",
        "The appointment is invalid.",
      );
    }
    return this.request<{
      note: { notes: string; revision: number; updatedAt: string };
    }>(
      `/organizations/${encodeURIComponent(organizationId)}/salons/agenda/appointments/${encodeURIComponent(appointmentId)}/notes`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      },
    ).then((payload) => payload.note);
  }

  transitionSalonAgendaAppointment(
    organizationId: string,
    appointmentId: string,
    status: Exclude<SalonAppointmentStatus, "pending" | "cancelled">,
  ): Promise<{ appointment: { id: string; status: SalonAppointmentStatus } }> {
    if (
      !ORGANIZATION_ID.test(organizationId) ||
      !/^cd_apt_[A-Za-z0-9._:-]+$/.test(appointmentId)
    ) {
      throw new PlatformApiError(
        400,
        "salons_appointment_invalid",
        "The appointment is invalid.",
      );
    }
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/salons/agenda/appointments/${encodeURIComponent(appointmentId)}/transition`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ status }),
      },
    );
  }

  async createSalonAppointment(
    organizationId: string,
    input: {
      serviceId: string;
      customerId: string | null;
      locationId: string | null;
      resourceId: string | null;
      startsAt: string;
      attendee: {
        name: string;
        email: string;
        timeZone: string;
        phoneNumber: string | null;
        language: string | null;
      };
    },
  ): Promise<unknown> {
    if (!ORGANIZATION_ID.test(organizationId) || !/^cd_svc_[A-Za-z0-9._:-]+$/.test(input.serviceId)) {
      throw new PlatformApiError(400, "salons_appointment_invalid", "The appointment is invalid.");
    }
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/scheduling/appointments`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "idempotency-key": `salons-web-create-${crypto.randomUUID()}`,
      },
      body: JSON.stringify(input),
    });
  }

  async rescheduleSalonAppointment(
    organizationId: string,
    appointmentId: string,
    input: { startsAt: string; timeZone: string; reason: string | null; rescheduledBy: string | null },
  ): Promise<unknown> {
    if (!ORGANIZATION_ID.test(organizationId) || !/^cd_apt_[A-Za-z0-9._:-]+$/.test(appointmentId)) {
      throw new PlatformApiError(400, "salons_appointment_invalid", "The appointment is invalid.");
    }
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/scheduling/appointments/${encodeURIComponent(appointmentId)}/reschedule`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "idempotency-key": `salons-web-reschedule-${crypto.randomUUID()}`,
      },
      body: JSON.stringify(input),
    });
  }

  async cancelSalonAppointment(
    organizationId: string,
    appointmentId: string,
    input: { reason: string | null },
  ): Promise<unknown> {
    if (!ORGANIZATION_ID.test(organizationId) || !/^cd_apt_[A-Za-z0-9._:-]+$/.test(appointmentId)) {
      throw new PlatformApiError(400, "salons_appointment_invalid", "The appointment is invalid.");
    }
    return this.request(`/organizations/${encodeURIComponent(organizationId)}/scheduling/appointments/${encodeURIComponent(appointmentId)}/cancel`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "idempotency-key": `salons-web-cancel-${crypto.randomUUID()}`,
      },
      body: JSON.stringify(input),
    });
  }

  prospectDemoRequests(
    status: ProspectDemoLifecycleStatus | null = null,
    limit = 100,
  ): Promise<{ requests: readonly ProspectDemoRequestRecord[] }> {
    if (!Number.isSafeInteger(limit) || limit < 1 || limit > 200) {
      throw new PlatformApiError(
        400,
        "prospect_demo_limit_invalid",
        "The Prospect Demo request limit is invalid.",
      );
    }
    const params = new URLSearchParams({ limit: String(limit) });
    if (status) params.set("status", status);
    return this.request(
      `/prospect-demo-requests?${params.toString()}`,
    );
  }

  prospectDemoRequest(
    requestId: string,
  ): Promise<ProspectDemoRequestDetail> {
    if (!PROSPECT_DEMO_REQUEST_ID.test(requestId)) {
      throw new PlatformApiError(
        400,
        "prospect_demo_request_id_invalid",
        "The Prospect Demo request id is invalid.",
      );
    }
    return this.request(
      `/prospect-demo-requests/${encodeURIComponent(requestId)}`,
    );
  }

  acceptProspectDemoRequest(
    requestId: string,
    creationPath: Exclude<ProspectDemoCreationPath, "undecided">,
    expectedVersion: number,
  ): Promise<{
    request: ProspectDemoRequestRecord;
    replayed: boolean;
  }> {
    if (!PROSPECT_DEMO_REQUEST_ID.test(requestId)) {
      throw new PlatformApiError(
        400,
        "prospect_demo_request_id_invalid",
        "The Prospect Demo request id is invalid.",
      );
    }
    return this.request(
      `/prospect-demo-requests/${encodeURIComponent(requestId)}`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          action: "accept",
          creationPath,
          expectedVersion,
        }),
      },
    );
  }

  executeProspectDemoRequest(
    requestId: string,
    expectedVersion: number,
    targetUrl?: string | null,
  ): Promise<{
    request: ProspectDemoRequestRecord;
    execution: ProspectDemoExecutionAttempt;
    replayed: boolean;
  }> {
    if (!PROSPECT_DEMO_REQUEST_ID.test(requestId)) {
      throw new PlatformApiError(
        400,
        "prospect_demo_request_id_invalid",
        "The Prospect Demo request id is invalid.",
      );
    }
    return this.request(
      `/prospect-demo-requests/${encodeURIComponent(requestId)}`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          action: "execute",
          expectedVersion,
          ...(targetUrl ? { targetUrl } : {}),
        }),
      },
    );
  }

  cancelProspectDemoRequest(
    requestId: string,
    expectedVersion: number,
  ): Promise<{
    request: ProspectDemoRequestRecord;
    replayed: boolean;
  }> {
    if (!PROSPECT_DEMO_REQUEST_ID.test(requestId)) {
      throw new PlatformApiError(
        400,
        "prospect_demo_request_id_invalid",
        "The Prospect Demo request id is invalid.",
      );
    }
    return this.request(
      `/prospect-demo-requests/${encodeURIComponent(requestId)}`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          action: "cancel",
          expectedVersion,
        }),
      },
    );
  }

  solutionFactoryTemplates(): Promise<{ templates: readonly SolutionBlueprintV1[] }> {
    return this.request("/solution-factory/templates");
  }

  solutionFactoryTemplate(templateKey: string): Promise<{ template: SolutionBlueprintV1 }> {
    if (!/^[a-z0-9](?:[a-z0-9._:-]{0,95})$/.test(templateKey)) {
      throw new PlatformApiError(
        400,
        "solution_template_key_invalid",
        "The Solution Factory template key is invalid.",
      );
    }
    return this.request(
      `/solution-factory/templates/${encodeURIComponent(templateKey)}`,
    );
  }

  validateSolutionFactoryBlueprint(
    blueprint: SolutionBlueprintV1,
  ): Promise<{ validation: SolutionContractValidation }> {
    return this.request("/solution-factory/validate", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ blueprint }),
    });
  }

  solutionFactoryCanvases(
    limit = 100,
  ): Promise<{ canvases: readonly SolutionFactoryCanvas[] }> {
    if (!Number.isSafeInteger(limit) || limit < 1 || limit > 200) {
      throw new PlatformApiError(
        400,
        "solution_canvas_limit_invalid",
        "The Solution Factory canvas limit is invalid.",
      );
    }
    return this.request(
      `/solution-factory/canvases?limit=${encodeURIComponent(String(limit))}`,
    );
  }

  solutionFactoryCanvas(
    canvasId: string,
  ): Promise<{ canvas: SolutionFactoryCanvas }> {
    if (!/^cd_sfcv_[a-f0-9]{32}$/.test(canvasId)) {
      throw new PlatformApiError(
        400,
        "solution_canvas_id_invalid",
        "The Solution Factory canvas id is invalid.",
      );
    }
    return this.request(
      `/solution-factory/canvases/${encodeURIComponent(canvasId)}`,
    );
  }

  createSolutionFactoryCanvas(
    canvas: SolutionCanvasDraftV1,
  ): Promise<{ canvas: SolutionFactoryCanvas }> {
    return this.request("/solution-factory/canvases", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ canvas }),
    });
  }

  updateSolutionFactoryCanvas(
    canvasId: string,
    canvas: SolutionCanvasDraftV1,
    expectedVersion: number,
  ): Promise<{ canvas: SolutionFactoryCanvas }> {
    if (!/^cd_sfcv_[a-f0-9]{32}$/.test(canvasId)) {
      throw new PlatformApiError(
        400,
        "solution_canvas_id_invalid",
        "The Solution Factory canvas id is invalid.",
      );
    }
    if (!Number.isSafeInteger(expectedVersion) || expectedVersion < 1) {
      throw new PlatformApiError(
        400,
        "solution_canvas_version_invalid",
        "The Solution Factory canvas version is invalid.",
      );
    }
    return this.request(
      `/solution-factory/canvases/${encodeURIComponent(canvasId)}`,
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ canvas, expectedVersion }),
      },
    );
  }

  compileSolutionFactoryCanvas(
    canvasId: string,
    expectedVersion: number,
    templateKey = "custom-solution",
  ): Promise<
    | {
        compiled: true;
        canvasId: string;
        canvasVersion: number;
        draft: SolutionFactoryDraft;
      }
    | {
        compiled: false;
        issues: readonly SolutionCanvasIssue[];
      }
  > {
    if (!/^cd_sfcv_[a-f0-9]{32}$/.test(canvasId)) {
      throw new PlatformApiError(
        400,
        "solution_canvas_id_invalid",
        "The Solution Factory canvas id is invalid.",
      );
    }
    if (!Number.isSafeInteger(expectedVersion) || expectedVersion < 1) {
      throw new PlatformApiError(
        400,
        "solution_canvas_version_invalid",
        "The Solution Factory canvas version is invalid.",
      );
    }
    return this.request(
      `/solution-factory/canvases/${encodeURIComponent(canvasId)}/compile`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ expectedVersion, templateKey }),
      },
    );
  }

  solutionFactoryDrafts(limit = 100): Promise<{ drafts: readonly SolutionFactoryDraft[] }> {
    if (!Number.isSafeInteger(limit) || limit < 1 || limit > 200) {
      throw new PlatformApiError(
        400,
        "solution_draft_limit_invalid",
        "The Solution Factory draft limit is invalid.",
      );
    }
    return this.request(
      `/solution-factory/drafts?limit=${encodeURIComponent(String(limit))}`,
    );
  }

  solutionFactoryDraft(draftId: string): Promise<{ draft: SolutionFactoryDraft }> {
    if (!/^cd_sfd_[a-f0-9]{32}$/.test(draftId)) {
      throw new PlatformApiError(
        400,
        "solution_draft_id_invalid",
        "The Solution Factory draft id is invalid.",
      );
    }
    return this.request(
      `/solution-factory/drafts/${encodeURIComponent(draftId)}`,
    );
  }

  createSolutionFactoryDraft(
    blueprint: SolutionBlueprintV1,
  ): Promise<{ draft: SolutionFactoryDraft }> {
    return this.request("/solution-factory/drafts", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ blueprint }),
    });
  }

  updateSolutionFactoryDraft(
    draftId: string,
    blueprint: SolutionBlueprintV1,
    expectedVersion: number,
  ): Promise<{ draft: SolutionFactoryDraft }> {
    if (!/^cd_sfd_[a-f0-9]{32}$/.test(draftId)) {
      throw new PlatformApiError(
        400,
        "solution_draft_id_invalid",
        "The Solution Factory draft id is invalid.",
      );
    }
    if (!Number.isSafeInteger(expectedVersion) || expectedVersion < 1) {
      throw new PlatformApiError(
        400,
        "solution_draft_version_invalid",
        "The Solution Factory draft version is invalid.",
      );
    }
    return this.request(
      `/solution-factory/drafts/${encodeURIComponent(draftId)}`,
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ blueprint, expectedVersion }),
      },
    );
  }

  solutionFactoryCertifications(
    limit = 100,
  ): Promise<{ certifications: readonly SolutionFactoryCertification[] }> {
    if (!Number.isSafeInteger(limit) || limit < 1 || limit > 200) {
      throw new PlatformApiError(
        400,
        "solution_certification_limit_invalid",
        "The Solution Factory certification limit is invalid.",
      );
    }
    return this.request(
      `/solution-factory/certified?limit=${encodeURIComponent(String(limit))}`,
    );
  }

  solutionFactoryCertification(
    certificationId: string,
  ): Promise<{ certification: SolutionFactoryCertification }> {
    if (!SOLUTION_CERTIFICATION_ID.test(certificationId)) {
      throw new PlatformApiError(
        400,
        "solution_certification_id_invalid",
        "The Solution Factory certification id is invalid.",
      );
    }
    return this.request(
      `/solution-factory/certified/${encodeURIComponent(certificationId)}`,
    );
  }

  certifySolutionFactoryDraft(
    draftId: string,
    expectedVersion: number,
  ): Promise<{ certification: SolutionFactoryCertification }> {
    if (!/^cd_sfd_[a-f0-9]{32}$/.test(draftId)) {
      throw new PlatformApiError(
        400,
        "solution_draft_id_invalid",
        "The Solution Factory draft id is invalid.",
      );
    }
    if (!Number.isSafeInteger(expectedVersion) || expectedVersion < 1) {
      throw new PlatformApiError(
        400,
        "solution_draft_version_invalid",
        "The Solution Factory draft version is invalid.",
      );
    }
    return this.request(
      `/solution-factory/drafts/${encodeURIComponent(draftId)}/certify`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ expectedVersion }),
      },
    );
  }

  createSolutionFactoryProvisioningPlan(
    certificationId: string,
    organizationId: string,
  ): Promise<{
    plan: SolutionFactoryProvisioningPlan;
    replayed: boolean;
  }> {
    if (
      !SOLUTION_CERTIFICATION_ID.test(certificationId) ||
      !ORGANIZATION_ID.test(organizationId)
    ) {
      throw new PlatformApiError(
        400,
        "solution_provisioning_target_invalid",
        "The Solution Factory provisioning target is invalid.",
      );
    }
    return this.request(
      `/solution-factory/certified/${encodeURIComponent(certificationId)}/provisioning-plans`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ organizationId }),
      },
    );
  }

  solutionFactoryProvisioningPlan(
    planId: string,
  ): Promise<{
    plan: SolutionFactoryProvisioningPlan;
    revisions: readonly SolutionFactoryProvisioningPlanRevision[];
  }> {
    if (!SOLUTION_PROVISIONING_PLAN_ID.test(planId)) {
      throw new PlatformApiError(
        400,
        "solution_provisioning_plan_id_invalid",
        "The Solution Factory provisioning plan id is invalid.",
      );
    }
    return this.request(
      `/solution-factory/provisioning-plans/${encodeURIComponent(planId)}`,
    );
  }

  refreshSolutionFactoryProvisioningPlan(
    planId: string,
    expectedVersion: number,
  ): Promise<{ plan: SolutionFactoryProvisioningPlan }> {
    if (
      !SOLUTION_PROVISIONING_PLAN_ID.test(planId) ||
      !Number.isSafeInteger(expectedVersion) ||
      expectedVersion < 1
    ) {
      throw new PlatformApiError(
        400,
        "solution_provisioning_refresh_invalid",
        "The Solution Factory provisioning refresh is invalid.",
      );
    }
    return this.request(
      `/solution-factory/provisioning-plans/${encodeURIComponent(planId)}/refresh`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ expectedVersion }),
      },
    );
  }

  solutionFactoryProvisioningApply(
    planId: string,
  ): Promise<SolutionFactoryProvisioningApplyState> {
    if (!SOLUTION_PROVISIONING_PLAN_ID.test(planId)) {
      throw new PlatformApiError(
        400,
        "solution_provisioning_plan_id_invalid",
        "The Solution Factory provisioning plan id is invalid.",
      );
    }
    return this.request(
      `/solution-factory/provisioning-plans/${encodeURIComponent(planId)}/apply`,
    );
  }

  applySolutionFactoryProvisioning(
    planId: string,
    expectedPlanVersion: number,
    action: SolutionFactoryProvisioningApplyAction,
    idempotencyKey: string,
  ): Promise<{
    receipt: SolutionFactoryProvisioningApplyReceipt;
    plan: SolutionFactoryProvisioningPlan;
    replayed: boolean;
  }> {
    if (
      !SOLUTION_PROVISIONING_PLAN_ID.test(planId) ||
      !Number.isSafeInteger(expectedPlanVersion) ||
      expectedPlanVersion < 1 ||
      !/^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$/.test(idempotencyKey)
    ) {
      throw new PlatformApiError(
        400,
        "solution_provisioning_apply_invalid",
        "The Solution Factory provisioning Apply request is invalid.",
      );
    }
    return this.request(
      `/solution-factory/provisioning-plans/${encodeURIComponent(planId)}/apply`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": idempotencyKey,
        },
        body: JSON.stringify({ expectedPlanVersion, action }),
      },
    );
  }

  solutionFactoryInstances(
    organizationId: string,
  ): Promise<{ instances: readonly SolutionFactoryInstance[] }> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(
        400,
        "solution_instance_organization_invalid",
        "The Solution Factory organization is invalid.",
      );
    }
    return this.request(
      `/solution-factory/solution-instances?organizationId=${encodeURIComponent(organizationId)}`,
    );
  }

  createSolutionFactoryInstance(
    planId: string,
    expectedPlanVersion: number,
  ): Promise<{
    instance: SolutionFactoryInstance;
    replayed: boolean;
    activationPerformed: false;
  }> {
    if (
      !SOLUTION_PROVISIONING_PLAN_ID.test(planId) ||
      !Number.isSafeInteger(expectedPlanVersion) ||
      expectedPlanVersion < 1
    ) {
      throw new PlatformApiError(
        400,
        "solution_instance_create_invalid",
        "The Solution Factory instance request is invalid.",
      );
    }
    return this.request(
      `/solution-factory/provisioning-plans/${encodeURIComponent(planId)}/solution-instance`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ expectedPlanVersion }),
      },
    );
  }

  solutionFactoryInstance(
    instanceId: string,
  ): Promise<{
    instance: SolutionFactoryInstance;
    activation: SolutionFactoryActivationState["activation"];
    control: SolutionFactoryActivationControl | null;
    activationPerformed: boolean;
    runtimeAuthorization: SolutionFactoryRuntimeAuthorization | null;
  }> {
    if (!SOLUTION_INSTANCE_ID.test(instanceId)) {
      throw new PlatformApiError(
        400,
        "solution_instance_id_invalid",
        "The Solution Factory instance id is invalid.",
      );
    }
    return this.request(
      `/solution-factory/solution-instances/${encodeURIComponent(instanceId)}`,
    );
  }

  solutionFactoryActivationState(
    instanceId: string,
  ): Promise<SolutionFactoryActivationState> {
    if (!SOLUTION_INSTANCE_ID.test(instanceId)) {
      throw new PlatformApiError(
        400,
        "solution_instance_id_invalid",
        "The Solution Factory instance id is invalid.",
      );
    }
    return this.request(
      `/solution-factory/solution-instances/${encodeURIComponent(instanceId)}/activation`,
    );
  }

  activateSolutionFactoryInstance(
    instanceId: string,
    expectedControlVersion: number | null = null,
  ): Promise<{
    activation: SolutionFactoryActivationState["activation"];
    control: SolutionFactoryActivationControl;
    action: "activate" | "reactivate" | "switch";
    replayed: boolean;
    runtimeAuthorization: SolutionFactoryRuntimeAuthorization;
  }> {
    if (
      !SOLUTION_INSTANCE_ID.test(instanceId) ||
      (
        expectedControlVersion !== null &&
        (
          !Number.isSafeInteger(expectedControlVersion) ||
          expectedControlVersion < 1
        )
      )
    ) {
      throw new PlatformApiError(
        400,
        "solution_activation_request_invalid",
        "The Solution Factory activation request is invalid.",
      );
    }
    return this.request(
      `/solution-factory/solution-instances/${encodeURIComponent(instanceId)}/activate`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(
          expectedControlVersion === null
            ? {}
            : { expectedControlVersion },
        ),
      },
    );
  }

  deactivateSolutionFactoryInstance(
    instanceId: string,
    expectedControlVersion: number,
  ): Promise<{
    control: SolutionFactoryActivationControl;
    replayed: boolean;
    runtimeAuthorization: SolutionFactoryRuntimeAuthorization;
  }> {
    if (
      !SOLUTION_INSTANCE_ID.test(instanceId) ||
      !Number.isSafeInteger(expectedControlVersion) ||
      expectedControlVersion < 1
    ) {
      throw new PlatformApiError(
        400,
        "solution_deactivation_request_invalid",
        "The Solution Factory deactivation request is invalid.",
      );
    }
    return this.request(
      `/solution-factory/solution-instances/${encodeURIComponent(instanceId)}/deactivate`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ expectedControlVersion }),
      },
    );
  }

}
